import { action, observable } from 'mobx'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import { Model, type ModelProps } from '@/infra/model'
import { unixEpoch } from '@/utils/date-util'
import {
  WorkspaceApplicant,
  type WorkspaceApplicantProps,
} from './WorkspaceApplicant'

export type WorkspacePostingJobType = 'Prep' | 'Review' | 'Bookkeeping' | 'Null'

export type WorkspacePostingStatus =
  'Draft' | 'Active' | 'Closed' | 'Cancelled' | 'InReview' | 'Null'

export type WorkspacePostingOpportunityType =
  'Unknown' | 'SignAndFile' | 'Outsourcing'

export interface WorkspacePostingProps extends ModelProps<WorkspacePosting> {
  applicants?: WorkspaceApplicantProps[]
}

export class WorkspacePosting extends Model<WorkspacePosting> {
  id = Id.none

  @observable accessor workspaceId = Id.none
  @observable accessor workspace: Workspace = Workspace.none
  @observable accessor dateCreated: Date = unixEpoch()
  @observable accessor dateModified: Date = unixEpoch()
  @observable accessor location = ''
  @observable accessor primarySoftware = ''
  @observable accessor supplementalSoftware = ''
  @observable accessor backupFileNeeded = false
  @observable accessor specialties: string[] = []
  @observable accessor capacity = 0
  @observable accessor maxAllowedWorkspaces = 0
  @observable accessor jobTypes: WorkspacePostingJobType[] = []
  @observable accessor description = ''
  @observable accessor opportunityType: WorkspacePostingOpportunityType =
    'Unknown'

  @observable accessor status: WorkspacePostingStatus = 'Null'

  readonly applicants = collection({
    getDataId: (d) => d.userPublicId,
    getModelId: (m) => m.userPublicId,
    create: () => new WorkspaceApplicant(),
  })

  @action.bound
  override set({ applicants, ...props }: Partial<WorkspacePostingProps>): this {
    this.applicants.reset(applicants)
    return super.set(props)
  }
}
